import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'redux-bundler-react';
import StepHeader from '@components/step-header/StepHeader';
import { Button, Alert } from '@trussworks/react-uswds';
import DatePicker from '@components/date-picker/DatePicker';
import TextInput from '@components/text-input/TextInput';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { filterNullEmptyObjects, doClearIncompletePhoneInfo } from '@src/utils/helpers';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Section from '@components/section/Section';
import { useNpr, useRequest, useUserRoleCapabilities } from '@stores';
import { useResetDirtyFields } from '@hooks';
import { useUserProfile } from '@hooks/useUserProfile';
import { RequestType, NprResultTypes, RequestStatus } from '@src/utils/enums';
import { getRequestOwner } from '@api/services/authorized-user/helper';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';
import useGetAuthorizedUsers from '@api/services/authorized-user/hooks/useGetAuthorizedUsers';
import { useLookupTableFormVersions } from '@api/services/lookup/hooks';
import { resolveNprConfig } from '@src/app-pages/Forms/registry/configs/npr/nprConfig';
import CertifySignSubmitRoleAlert from '@forms/input-forms/common/CertifySignSubmitRoleAlert';

export const NprFormCertifySignSubmitMetadata = {
  sectionName: 'Certify, Sign, and Submit',
};

const resultMessages = {
  [NprResultTypes.PermitRequired]: 'requires a Permit',
  [RequestType.PreApplication]: 'requires a Pre-application Meeting',
  [NprResultTypes.NprLetter]: 'does not require a Permit',
  [NprResultTypes.NprExemption]: `qualifies for a ${NprResultTypes.NprExemption}`,
  [NprResultTypes.JurisdictionalDetermination]: 'requires a Jurisdictional Determination',
};

const resultActions = {
  [NprResultTypes.PermitRequired]: 'request an Application for Department of the Army (DA) Permit',
  [RequestType.PreApplication]: 'request a Pre-application/Project Scoping',
  [NprResultTypes.NprLetter]: 'download your letter',
  [NprResultTypes.NprExemption]: 'download your letter',
  [NprResultTypes.JurisdictionalDetermination]: 'request a Jurisdictional Determination',
};

const NprFormCertifySignSubmit = connect(
  'doUpdateRequestObject',
  'doUploadAndUpdateNpr',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectNprErrors',
  'selectRequestID',
  'selectRequestType',
  'selectRequestStatus',
  ({
    doUpdateRequestObject,
    doUploadAndUpdateNpr,
    requestAPIData,
    requestFormData,
    nprErrors,
    stepNo,
    requestID,
    requestType,
    version,
    requestStatus,
    formVariant,
  }) => {
    const { data: authorizedUsersData } = useGetAuthorizedUsers({ requestId: requestID, version });
    const { userProfileData } = useUserProfile();
    const { nprResult } = useNpr();
    const lookupTableFormVersions = useLookupTableFormVersions();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched, selectedRequestId, sections } = useRequest();
    const canSign = useUserRoleCapabilities((state) => state.roleCapabilities.signSubmitRequest);

    const authorizedUsers = authorizedUsersData ?? [];
    const requestOwner = getRequestOwner(authorizedUsers);
    const isRequestOwner = requestAPIData?.request?.permission === AuthorizedUserPermissions.Owner;
    const canUserSign = canSign && isRequestOwner;
    const hasSubmitted = requestStatus !== RequestStatus.Draft;
    const formVersion = lookupTableFormVersions.find(
      (item) => item.requestType === RequestType.ActivityBasedNoPermitRequired
    )?.formVersion;

    // Signature always validated against user profile name
    const getName = useCallback(
      () =>
        userProfileData?.middleName
          ? `${userProfileData.firstName} ${userProfileData.middleName} ${userProfileData.lastName}`
          : `${userProfileData?.firstName} ${userProfileData?.lastName}`,
      [userProfileData?.firstName, userProfileData?.middleName, userProfileData?.lastName]
    );

    const defaultValues = {
      signature: requestAPIData?.request?.signature ?? null,
      signatureDate:
        requestAPIData?.request?.signatureDate !== null
          ? requestAPIData?.request?.signatureDate?.split('T')[0]
          : ('' ?? ''),
    };

    const [isSigned, setIsSigned] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const schema = yup.object().shape({
      signature: yup.string().test('isSigned', `Signature must be ${getName()}`, (val) => val.trim() === getName()),
      signatureDate: yup.string().required('Please select a date'),
    });

    const methods = useForm({ defaultValues: defaultValues, resolver: yupResolver(schema), mode: 'onBlur' });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      handleSubmit,
      trigger,
      reset,
    } = methods;
    const isDirty = Object.keys(dirtyFields).length > 0;
    const errorCount = Object.values(errors).length;
    const isTouched = isSectionTouched(stepNo);
    const variant = formVariant ?? (requestType === RequestType.ActivityBasedNoPermitRequired ? 'abNpr' : 'npr');
    const versionForConfig = formVersion ?? 1;
    const { getCertifySignSubmit } = resolveNprConfig(variant, versionForConfig);
    const certifyContent = getCertifySignSubmit({ variant });
    const showAgentBullet = selectedRequestId === '1';
    const signature = watch('signature');
    const signatureDate = watch('signatureDate');
    const dateNow = new Date().toISOString().slice(0, 10);

    const resultMessage = useMemo(() => {
      const message = resultMessages[nprResult] || '';
      return message;
    }, [nprResult]);

    const actionMessage = useMemo(() => resultActions[nprResult], [nprResult]);

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const handleBlur = (e) => {
      setValue('signature', e.target.value.trim());
    };

    useEffect(() => {
      if (signature?.trim() === getName()) {
        !signatureDate && setValue('signatureDate', new Date().toISOString().slice(0, 10));
        setIsSigned(true);
        trigger();
      } else {
        setIsSigned(false);
      }
    }, [signature, getName, setValue, trigger, signatureDate]);

    const submitForm = () => {
      const requestFormDataSubmit = {
        ...requestFormData,
        request: { ...requestFormData.request, isSubmit: true, formVersion: formVersion },
      };
      const cleanedPayload = doClearIncompletePhoneInfo(requestFormDataSubmit);
      const filteredRequest = filterNullEmptyObjects(cleanedPayload);
      doUploadAndUpdateNpr(filteredRequest, 'Submit');
    };

    // Consolidated effect: Calculate form validity and update section state
    useEffect(() => {
      const hasInvalidSections = sections.some(
        (section) => section.valid === false && section.label !== 'Certify, Sign, and Submit'
      );
      const newIsFormValid = !hasInvalidSections;

      // Only update if state actually changed to prevent cascading re-renders
      if (newIsFormValid !== isFormValid) {
        setIsFormValid(newIsFormValid);
      }

      // Update section with all props at once
      updateSection(stepNo, {
        dirty: isDirty,
        valid: newIsFormValid && isSigned,
        label: NprFormCertifySignSubmitMetadata.sectionName,
      });

      // Only update request on successful completion
      if (newIsFormValid && isSigned && signature && signatureDate) {
        doUpdateRequestObject({ signature, signatureDate });
      }
    }, [
      stepNo,
      isDirty,
      isSigned,
      isFormValid,
      signature,
      signatureDate,
      sections,
      doUpdateRequestObject,
      updateSection,
    ]);

    const renderSignatureSection = () => {
      if (!isFormValid) {
        return (
          <Alert className='margin-top-3' slim type='error'>
            In order to submit this form, please revisit the section(s) marked with errors, and correct any missing or
            invalid fields.
          </Alert>
        );
      }

      return (
        <Section label='Signature'>
          <div className='grid-row grid-gap-1'>
            <div className='grid-col-6'>
              <TextInput
                name='signature'
                label='Signature'
                signature={canUserSign && getName()}
                required
                readOnly={isReadOnly || !canUserSign}
                onBlur={handleBlur}
              />
            </div>
            <div className='grid-col-6'>
              <DatePicker
                min={null}
                max={dateNow}
                name='signatureDate'
                label='Signature Date'
                readOnly={isReadOnly || !canUserSign}
                required
              />
            </div>
          </div>
          <hr />
          <div className='display-flex flex-justify-start padding-top-1'>
            <Button
              className='submit-request-button'
              title='Submit Request'
              disabled={!isSigned || isReadOnly || !canUserSign}
              onClick={handleSubmit(submitForm)}
            >
              Submit Request
            </Button>
          </div>
        </Section>
      );
    };

    return (
      <FormProvider {...methods}>
        {(!isFormValid || errorCount > 0) && !isReadOnly && <ErrorSummary errors={errors} type='form' />}
        {Object.keys(nprErrors ?? {})?.length > 0 && <ErrorSummary errors={nprErrors} type='api-validation' />}
        {resultMessage && certifyContent.resultBanner?.({ resultMessage, actionMessage })}
        <CertifySignSubmitRoleAlert showAlert={!hasSubmitted && !canSign} />
        {!isRequestOwner && !hasSubmitted && certifyContent.notAuthorizedBanner?.({ requestOwner })}
        <StepHeader text={certifyContent.headerText}>{certifyContent.headerSubText}</StepHeader>
        <div className='text-bold'>
          <div className='grid-row margin-left-2'>
            <h6>{certifyContent.agreeHeading}</h6>
          </div>
          {certifyContent.agreeBullets({ showAgentBullet })}
        </div>
        {renderSignatureSection()}
      </FormProvider>
    );
  }
);

NprFormCertifySignSubmit.metadata = NprFormCertifySignSubmitMetadata;

export default NprFormCertifySignSubmit;
